import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Divider, Typography, Stack, MenuItem, useTheme, Switch } from '@mui/material';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import DashboardIcon from '@mui/icons-material/Dashboard';
// routes
import { PATH_AUTH } from '../../../routes/paths';
// hooks
import useAuth from '../../../hooks/useAuth';
import useIsMountedRef from '../../../hooks/useIsMountedRef';
// components
import MyAvatar from '../../../components/MyAvatar';
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';
import useSettings from '../../../hooks/useSettings';

// ----------------------------------------------------------------------

// const MENU_OPTIONS = [
//   // {
//   //   label: 'Home',
//   //   linkTo: '/',
//   // },
//   // {
//   //   label: 'Profile',
//   //   linkTo: PATH_DASHBOARD.user.profile,
//   // },
//   {
//     label: 'Settings',
//     linkTo: PATH_DASHBOARD.profile.root,
//   },
// ];

// ----------------------------------------------------------------------

export default function AccountPopover() {
  const navigate = useNavigate();

  const { logout, user } = useAuth();

  const userType = JSON.parse(localStorage.getItem('userType'));

  const isMountedRef = useIsMountedRef();

  const { enqueueSnackbar } = useSnackbar();

  const [open, setOpen] = useState(null);

  const { onToggleMode } = useSettings();

  const theme = useTheme();

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate(PATH_AUTH.login, { replace: true });

      if (isMountedRef.current) {
        handleClose();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Unable to logout!', { variant: 'error' });
    }
  };

  const [isDashboardV2, setIsDashboardV2] = useState(localStorage.getItem('dashboardVersion') === 'v2');
  const handleDashboardVersionChange = (event) => {
    const isV2 = event.target.checked;
    setIsDashboardV2(isV2);
    localStorage.setItem('dashboardVersion', isV2 ? 'v2' : 'v1');

    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event('dashboardVersionChanged'));

    // Optionally refresh the page or update the dashboard view
    window.location.reload();
  };

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpen}
        sx={{
          p: 0,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        <MyAvatar />
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="h6" noWrap sx={{ textTransform: 'uppercase' }}>
            {user?.username}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {userType?.isSuperAdmin && 'SuperAdmin'}
            {userType?.isAdmin && 'Admin'}
            {userType?.isBroker && 'Broker'}
            {userType?.isClient && 'Client'}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        {/* Theme Switch */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Light</Typography>
            <Switch checked={theme.palette.mode === 'dark'} onChange={onToggleMode} size="small" />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>Dark</Typography>
          </Box>
        </Stack>

        {/* Dashboard Version Switch */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ViewWeekIcon sx={{ fontSize: '1.1rem', color: isDashboardV2 ? 'text.secondary' : 'primary.main' }} />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>v1</Typography>
            <Switch
              checked={isDashboardV2}
              onChange={handleDashboardVersionChange}
              size="small"
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: 'primary.main',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: 'primary.main',
                },
              }}
            />
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>v2</Typography>
            <DashboardIcon sx={{ fontSize: '1.1rem', color: isDashboardV2 ? 'primary.main' : 'text.secondary' }} />
          </Box>
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem onClick={handleLogout} sx={{ m: 1 }}>
          Logout
        </MenuItem>
      </MenuPopover>
    </>
  );
}
